import { type FC, useRef, useEffect, useState } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { IconChevronDown } from "@/assets/icons/geist/IconChevronDown";
import { IconCircleCheckedFilled } from "@/assets/icons/central/IconCircleCheckedFilled";
import { IconCircle } from "@/assets/icons/geist/IconCircle";
import { OnaThinking } from "@/components/ona/OnaThinking";
import { TextShimmer } from "@/components/flexkit/TextShimmer";
import { AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import type { AgentMessageFragment, AgentMessageFragmentType } from "@/hooks/use-stream-conversation";
import type { AgentTodoGroupItem } from "@/utils/message-grouping";
import { Fragment } from "@/components/ai/Fragment";
import type { PlainAgentExecution } from "@/queries/agent-queries";

export type TodoContainerProps = {
    todo: AgentTodoGroupItem;
    isLast: boolean;
    agentExecution: PlainAgentExecution;
    onClick?: (block: AgentMessageFragment) => void;
};

export const TodoContainer: FC<TodoContainerProps> = ({ todo, isLast, agentExecution, onClick }) => {
    const { todoId, todoTitle, todoStatus, fragments } = todo;
    const fragmentCount = fragments.length;

    const [isOpen, setIsOpen] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const isPending = todoStatus === AgentResponseBlock_TodoItem_Phase.PENDING;
    const isInProgress = todoStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS;
    const isCompleted = todoStatus === AgentResponseBlock_TodoItem_Phase.DONE;
    const isCleared = todoStatus === AgentResponseBlock_TodoItem_Phase.CLEARED;

    // Animation state management - initialize based on current status
    const [animationState, setAnimationState] = useState<"entering" | "thinking" | "leaving">(() => {
        // If already completed on mount, skip animations
        if (isCompleted) return "leaving";
        // If in progress on mount, start with entering
        if (isInProgress) return "entering";
        return "entering";
    });
    const [showCheckmark, setShowCheckmark] = useState(isCompleted);
    const [isOnaExiting, setIsOnaExiting] = useState(false);
    const [hideDoneText, setHideDoneText] = useState(false);
    const prevStatusRef = useRef(todoStatus);

    // Check if children are empty (no fragments to display)
    const hasContent = fragments.length > 0;

    // Auto-scroll to bottom when in progress and is last
    useEffect(() => {
        if (isInProgress && isLast && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
    });

    // Handle status changes and animation transitions
    useEffect(() => {
        const prevStatus = prevStatusRef.current;
        prevStatusRef.current = todoStatus;

        if (isInProgress && prevStatus !== AgentResponseBlock_TodoItem_Phase.IN_PROGRESS) {
            // Todo just started - play enter animation
            setAnimationState("entering");
            setShowCheckmark(false);
            setHideDoneText(false);
        } else if (isCompleted && prevStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS) {
            // Todo just completed - play leave animation
            setAnimationState("leaving");
            setIsOnaExiting(true);
            setHideDoneText(false);

            // Start checkmark fade-in during the latter part of the exit animation
            // Exit animation is ~1050ms (frames 420-525 at 100fps)
            // Start fade at ~60% through (630ms) for tight transition
            const checkmarkTimer = setTimeout(() => {
                setShowCheckmark(true);
            }, 630);

            // Hide "Done" text after fade-out completes (630ms + 200ms fade = 830ms)
            const hideDoneTimer = setTimeout(() => {
                setHideDoneText(true);
            }, 830);

            return () => {
                clearTimeout(checkmarkTimer);
                clearTimeout(hideDoneTimer);
            };
        }
    }, [todoStatus, isInProgress, isCompleted]);

    // Todos are collapsed by default, no auto-collapse needed

    // Handle animation callbacks
    const handleEnterComplete = () => {
        // Enter animation done, transition to thinking loop
        setAnimationState("thinking");
    };

    const handleExitComplete = () => {
        // Exit animation done, ensure checkmark is visible
        // (checkmark fade-in starts during the exit animation via timeout)
        if (!showCheckmark) {
            setShowCheckmark(true);
        }

        // Clear the exiting flag so height can collapse
        setIsOnaExiting(false);

        // If there's a next todo, it will start its enter animation
        // This creates the "Ona moves between frames" effect
    };

    const getStatusIcon = () => {
        if (isPending || isCleared) {
            return <IconCircle size="sm" className="size-5 flex-shrink-0 text-content-muted" />;
        } else if (isCompleted) {
            // Cross-fade: show both during transition
            return (
                <div className="relative size-5">
                    {!showCheckmark && (
                        <div className="absolute inset-0">
                            <OnaThinking
                                state={animationState}
                                onExitComplete={handleExitComplete}
                                onEnterComplete={handleEnterComplete}
                                size="sm"
                                className="!size-5"
                            />
                        </div>
                    )}
                    <IconCircleCheckedFilled
                        className={cn(
                            "size-5 text-content-success transition-opacity duration-200 ease-out",
                            showCheckmark ? "opacity-100" : "opacity-0",
                        )}
                    />
                </div>
            );
        } else if (isInProgress) {
            return (
                <OnaThinking
                    state={animationState}
                    onExitComplete={handleExitComplete}
                    onEnterComplete={handleEnterComplete}
                    size="sm"
                    className="!size-5"
                />
            );
        }
        return null;
    };

    const getStatusLabel = () => {
        if (isCompleted) return "Completed";
        if (isInProgress) return "In progress";
        if (isCleared) return "Cleared";
        return "Pending";
    };

    return (
        <div
            id={`todo-${todoId}`}
            className={cn("border-b border-border-base last:border-b-0", { "border-b-0": isPending })}
        >
            {/* Todo Header */}
            <button
                onClick={() => {
                    const canExpand = hasContent && (fragmentCount === undefined || fragmentCount > 0);
                    if (canExpand) setIsOpen(!isOpen);
                }}
                className={cn(
                    "flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-all duration-300 ease-out",
                    hasContent && (fragmentCount === undefined || fragmentCount > 0) && "hover:bg-surface-muted",
                    (!hasContent || (fragmentCount !== undefined && fragmentCount === 0)) && "cursor-default",
                )}
                aria-expanded={hasContent && (fragmentCount === undefined || fragmentCount > 0) ? isOpen : undefined}
                aria-label={`${getStatusLabel()}: ${todoTitle}`}
                data-tracking-id="todo-container-toggle"
                disabled={!hasContent || (fragmentCount !== undefined && fragmentCount === 0)}
            >
                {/* Left side: Status icon + Text content (title + fragment preview) */}
                <div
                    className={cn("flex min-w-0 flex-1 items-center gap-2", {
                        "opacity-50": isPending || isCleared,
                    })}
                >
                    <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">{getStatusIcon()}</div>
                    <div className="flex min-w-0 flex-1 flex-col gap-0">
                        {isInProgress ? (
                            <div className="flex min-w-0 flex-1 flex-col gap-0">
                                <TextShimmer className="min-w-0 truncate text-base text-content-primary">
                                    {todoTitle}
                                </TextShimmer>
                                <TextShimmer className="min-w-0 truncate font-mono text-sm">
                                    {currentFragmentText(todo)}
                                </TextShimmer>
                            </div>
                        ) : isOnaExiting ? (
                            <div className="flex min-w-0 flex-1 flex-col gap-0">
                                <span className="min-w-0 truncate text-base text-content-primary">{todoTitle}</span>
                                {!hideDoneText && (
                                    <span
                                        className={cn(
                                            "min-w-0 truncate font-mono text-sm text-content-muted transition-opacity duration-200 ease-out",
                                            showCheckmark && "opacity-0",
                                        )}
                                    >
                                        Done
                                    </span>
                                )}
                            </div>
                        ) : (
                            <h2
                                className={cn(
                                    "min-w-0 truncate text-base text-content-primary transition-colors duration-300",
                                    {
                                        "text-content-muted": isCompleted || isPending || isCleared,
                                        "line-through": isCleared,
                                    },
                                )}
                            >
                                {todoTitle}
                            </h2>
                        )}
                    </div>
                </div>

                {/* Right side: Controls (chevron only) - vertically centered */}
                {!isPending && (
                    <div className="flex flex-shrink-0 items-center">
                        <IconChevronDown
                            size="sm"
                            className={cn(
                                "flex-shrink-0 transition-transform duration-200",
                                isOpen && "rotate-180",
                                (!hasContent || (fragmentCount !== undefined && fragmentCount === 0)) &&
                                    "cursor-not-allowed opacity-40",
                            )}
                        />
                    </div>
                )}
            </button>

            {/* Todo Content - Simple version without complex animations */}
            {isOpen && hasContent && (
                <div
                    ref={scrollContainerRef}
                    className={cn(
                        "w-full overflow-y-auto border-t border-border-base px-4 py-2 text-base",
                        "max-h-[40vh]",
                    )}
                >
                    <div className="flex flex-col gap-4">
                        {todo.fragments.map((fragment, fragIndex) => {
                            return (
                                <Fragment
                                    key={`${todo.todoId}-${fragIndex}`}
                                    fragment={fragment}
                                    agentExecution={agentExecution}
                                    shouldHighlightFragment={false}
                                    onClick={onClick}
                                    data-tracking-id="default-tracking-id"
                                    ignore={IgnoreFragments}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

// Todo items are handled at the grouping level, so we don't
// want to render them as fragments
const IgnoreFragments = new Set<AgentMessageFragmentType>(["todoItem"]);

function currentFragmentText(todo: AgentTodoGroupItem) {
    // Find the current active fragment (last one when in progress)
    const isInProgress = todo.todoStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS;
    const currentFragment =
        isInProgress && todo.fragments.length > 0 ? todo.fragments[todo.fragments.length - 1] : null;

    let text = "Thinking...";
    if (currentFragment && currentFragment.type === "text" && currentFragment.content) {
        text = currentFragment.content.split("\n")[0].substring(0, 100);
    } else if (currentFragment && currentFragment.type === "action" && currentFragment.intentDescription) {
        text = currentFragment.intentDescription;
    }
    return text;
}
